// The two decision cards of the Hiring Request (lane B2 S08), each titled with his step label (DS §3.4):
//   HM certification — "HM reviews, corrects and certifies." The Hiring Manager edits any field in place; every changed
//     value shows Original → Revised and needs the one Reason ("record old value, new value, actor, timestamp, and
//     reason"); Certify sends the corrections, Return sends the request back to Draft or Dormant.
//   HRBP approval — "Keep HRBP as final approver." Before certification Approve is drawn blocked (Certification required
//     first); after it the approver steps through what the Hiring Manager revised, attests the final posting validation
//     (and PayFactors when the JD changed: "HRBP updates PayFactors if JD changed") and accepts each adverse or unknown
//     check, then Approves or Returns. A server refusal is a red banner on the card, never a toast.
// Every Original and Revised value reads as the History reads it (F-80): the choice's word, the person's name, Yes or
// No, never the stored key.

import { useMemo, useState } from "react";
import { requestApprove, requestCertify, requestReturn, requestReview, type RequestOpenOutput } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Button, Card, CheckTile, CORE_LABELS, Diff, DIALOG_LABELS, Dialog, Disclosure, Field, Flex, G6_CAPABILITY_LABELS,
  HISTORY_LABELS, JOURNEY_LABELS, Recorded, REQUEST_STATUS, Segmented, Stack, TextArea, type DiffRow,
} from "@/mc";
import { changedColumns, draftOf, fieldsOf, NOT_CORRECTABLE, type RequestDraft } from "./compat";
import { FIELD_WORD, useSettledRead } from "./data";
import { Refusal, shownValue } from "./HiringRequest";
import { REQUEST_FIELD_LABELS as F, REQUEST_PAGE_LABELS as P, REQUEST_RULES as R } from "./labels";
import { RequestFields } from "./RequestFields";

/** The people a person value on this request may name: the people on its events and its intended Hiring Manager. */
function peopleOn(r: RequestOpenOutput): ReadonlyMap<string, string> {
  const names = new Map(r.events.filter((event) => event.by_id && event.by_name).map((event) => [event.by_id!, event.by_name!]));
  if (r.intended_hm_id && r.intended_hm_name) names.set(r.intended_hm_id, r.intended_hm_name);
  return names;
}

/** The Hiring Manager's corrections as Original → Revised rows, each value in his words. */
export function correctionRows(base: RequestDraft, draft: RequestDraft, names: ReadonlyMap<string, string>): DiffRow[] {
  const before = fieldsOf(base) as Record<string, unknown>;
  const changes = changedColumns(base, draft) as Record<string, unknown>;
  return Object.keys(changes).map((key) => ({
    label: FIELD_WORD[key] ?? key,
    old: shownValue(before[key], key, names),
    new: shownValue(changes[key], key, names),
  }));
}

/** One revision the HR Business Partner steps through, its two values in his words. */
export function revisionValues(
  version: { field: string; old_value: unknown; new_value: unknown }, names: ReadonlyMap<string, string>,
): { original: string; revised: string } {
  return { original: shownValue(version.old_value, version.field, names), revised: shownValue(version.new_value, version.field, names) };
}

export function Decisions({ r, mode }: { r: RequestOpenOutput; mode: "certify" | "decide" }) {
  const { me } = useSession();
  if (mode === "certify") return <Certification r={r} />;
  const can = r.can ?? {};
  if (can.decide && (r.status === "submitted" || r.status === "question_raised")) return <Approval r={r} />;
  if (r.status === "pending_hm_cert" && me && me.person_id === r.reviewing_hrbp_id) return <ApprovalBlocked />;
  return null;
}

// ---- HM certification --------------------------------------------------------------------------------------------------

function Certification({ r }: { r: RequestOpenOutput }) {
  const base = useMemo(() => draftOf(r), [r]);
  const [draft, setDraft] = useState<RequestDraft>(base);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [returning, setReturning] = useState(false);
  const certify = useWrite((input: Parameters<typeof requestCertify>[0], key: string) => requestCertify(input, key), [["request"]]);
  // the target compensation range is not among the certification's corrections (CorrectionFields): read-only here
  const changes = Object.fromEntries(Object.entries(changedColumns(base, draft)).filter(([key]) => !(NOT_CORRECTABLE as readonly string[]).includes(key)));
  const jdChanged = draft.job_description !== base.job_description;
  const rows: DiffRow[] = correctionRows(base, draft, peopleOn(r));
  if (jdChanged) rows.push({ label: P.currentJd, old: base.job_description || "—", new: draft.job_description || "—" });
  const corrected = rows.length > 0;
  const reasonOk = !corrected || reason.trim().length >= 3;

  const run = async () => {
    setError(null);
    try {
      await certify.mutateAsync({
        request_id: r.id,
        expected_version: r.row_version ?? null,
        corrections: Object.keys(changes).length ? changes : null,
        job_description: jdChanged ? draft.job_description : null,
        reason: corrected ? reason.trim() : null,
      });
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Stack gap={16}>
      <RequestFields
        draft={draft}
        onChange={setDraft}
        mode="edit"
        hmName={r.intended_hm_name ?? undefined}
        compMode={"target_comp_min" in r || "target_comp_max" in r ? "view" : "hidden"}
      />
      <Card title={JOURNEY_LABELS.hmCertification} sub={G6_CAPABILITY_LABELS.certifyRequest} tone="glow" id="req-card-certify">
        <Stack gap={12}>
          <Refusal error={error} />
          {corrected ? <Diff form="cols" heads="originalRevised" rows={rows} /> : null}
          {corrected ? (
            <Field label={HISTORY_LABELS.reason} required hint={R.hmCorrects}>
              <TextArea value={reason} onChange={(event) => setReason(event.target.value)} rows={2}  />
            </Field>
          ) : null}
          <Flex gap={8} wrap>
            <Button tone="primary" busy={certify.isPending} blocked={reasonOk ? undefined : { reason: R.hmCorrects }} onClick={() => void run()} >
              {ACTION_LABELS.certify}
            </Button>
            <Button tone="ghost" onClick={() => setReturning(true)} >{ACTION_LABELS.return}</Button>
          </Flex>
        </Stack>
      </Card>
      {returning ? <ReturnDialog r={r} onClose={() => setReturning(false)} /> : null}
    </Stack>
  );
}

// ---- HRBP approval -----------------------------------------------------------------------------------------------------

function ApprovalBlocked() {
  return (
    <Card title={JOURNEY_LABELS.hrbpApproval} id="req-card-approve">
      <Stack gap={8}>
        <Flex gap={8} wrap>
          <Button tone="primary" blocked={{ reason: CORE_LABELS.certificationRequiredFirst, describedBy: "req-approve-rule" }} >{ACTION_LABELS.approve}</Button>
        </Flex>
        <p className="mc-hint" id="req-approve-rule">{CORE_LABELS.certificationRequiredFirst}</p>
        <p className="mc-hint">{R.distinctCertification}</p>
      </Stack>
    </Card>
  );
}

interface Check { key: string; label: string; status: string; evidence: string }

function Approval({ r }: { r: RequestOpenOutput }) {
  const review = useSettledRead(["request", r.id, "review"], () => requestReview({ request_id: r.id }));
  const approve = useWrite((input: Parameters<typeof requestApprove>[0], key: string) => requestApprove(input, key), [["request"]]);
  const [validated, setValidated] = useState(false);
  const [payfactors, setPayfactors] = useState(false);
  const [accepted, setAccepted] = useState<readonly string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [returning, setReturning] = useState(false);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);

  const routedAt = [...r.events].reverse().find((event) => event.kind === "routed_to_hm")?.at ?? null;
  const versions = ((r.field_versions ?? []) as { field: string; old_value: unknown; new_value: unknown; seat?: string; reason?: string | null; revision_no?: number; at: string }[])
    .filter((version) => version.seat === "hm" && (version.revision_no ?? r.revision_no) === r.revision_no && version.old_value !== null && version.old_value !== undefined);
  const jdChanged = !!routedAt && ((r.jd_versions ?? []) as { at: string }[]).some((version) => String(version.at) > String(routedAt));
  const checks = ((review.data?.checks ?? []) as unknown as Check[]).filter((check) => review.data?.needs_acknowledgment.includes(check.key));
  const shown = versions[Math.min(step, versions.length) - 1];
  const shownValues = shown ? revisionValues(shown, peopleOn(r)) : null;

  const blocked = !validated
    ? { reason: R.requiredFields }
    : jdChanged && !payfactors
      ? { reason: R.payfactors }
      : checks.some((check) => !accepted.includes(check.key))
        ? { reason: R.finalApprover }
        : undefined;

  const run = async () => {
    if (!review.data) return;
    setError(null);
    try {
      await approve.mutateAsync({
        request_id: r.id,
        review_token: review.data.review_token,
        acknowledged: [...accepted],
        final_posting_validated: true,
        payfactors_updated: jdChanged ? true : null,
      });
      setDone(true);
    } catch (e) {
      setError(e);
      void review.refetch();
    }
  };

  return (
    <Card title={JOURNEY_LABELS.hrbpApproval} tone="glow" id="req-card-approve" loading={review.isLoading ? { lines: 4 } : undefined}>
      <Stack gap={12}>
        <Refusal error={error ?? review.error} />
        {versions.length > 0 ? (
          <Disclosure title={P.revisedByHm} right={<span className="mc-mono">{versions.length}</span>} defaultOpen >
            {shown && shownValues ? (
              <Diff
                original={shownValues.original}
                revised={shownValues.revised}
                reason={shown.reason ?? undefined}
                actor={FIELD_WORD[shown.field] ?? shown.field}
                at={shown.at}
                step={{ index: Math.min(step, versions.length), count: versions.length, onStep: setStep, hs: "req.revision-prev", nextHs: "req.revision-next" }}
              />
            ) : null}
          </Disclosure>
        ) : null}
        <Stack gap={8}>
          <CheckTile checked={validated} onChange={setValidated} >{F.finalPostingValidation}</CheckTile>
          {jdChanged ? <CheckTile checked={payfactors} onChange={setPayfactors} >{R.payfactors}</CheckTile> : null}
          {checks.map((check) => (
            <CheckTile
              key={check.key}
              checked={accepted.includes(check.key)}
              onChange={(on) => setAccepted(on ? [...accepted, check.key] : accepted.filter((key) => key !== check.key))}
              sub={check.evidence}
              hs={`req.check.${check.key.replace(/_/g, "-")}`}
            >
              {check.label}
            </CheckTile>
          ))}
        </Stack>
        <Flex gap={8} wrap>
          <Button tone="primary" busy={approve.isPending} blocked={blocked} onClick={() => void run()} >{ACTION_LABELS.approve}</Button>
          <Button tone="ghost" onClick={() => setReturning(true)} >{ACTION_LABELS.return}</Button>
        </Flex>
        {done ? <Recorded live>{CORE_LABELS.recordedAuditEvent}</Recorded> : null}
      </Stack>
      {returning ? <ReturnDialog r={r} onClose={() => setReturning(false)} /> : null}
    </Card>
  );
}

// ---- Return: back to Draft or Dormant, history kept --------------------------------------------------------------------

const FLAGGABLE = [
  "title", "reason", "vacating_person_ref", "locations", "openings_requested", "is_people_manager", "posting_scope", "notes", "department",
  "job_family", "area_of_interest", "job_code", "org_alignment", "budget_status", "cost_center", "justification", "job_description",
] as const;

function ReturnDialog({ r, onClose }: { r: RequestOpenOutput; onClose: () => void }) {
  const send = useWrite((input: Parameters<typeof requestReturn>[0], key: string) => requestReturn(input, key), [["request"]]);
  const [note, setNote] = useState("");
  const [to, setTo] = useState<"draft" | "dormant">("draft");
  const [flagged, setFlagged] = useState<readonly string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const blocked = note.trim().length < 3 || flagged.length === 0 ? { reason: R.returned } : undefined;
  return (
    <Dialog
      title={ACTION_LABELS.return}
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{
        label: ACTION_LABELS.return,
        blocked,
        busy: send.isPending,
        hs: "req.return-confirm",
        onClick: () => {
          setError(null);
          send.mutateAsync({ request_id: r.id, note: note.trim(), to, fields_flagged: [...flagged] }).then(onClose, setError);
        },
      }}
      hint={R.returned}
    >
      <Stack gap={12}>
        <Refusal error={error} />
        <Field label={REQUEST_STATUS.returned} id="req-return-to">
          <Segmented
            aria-labelledby="req-return-to-l"
            value={to}
            options={[
              { value: "draft", label: REQUEST_STATUS.draft, hs: "req.return-to.draft" },
              { value: "dormant", label: REQUEST_STATUS.dormant, hs: "req.return-to.dormant" },
            ]}
            onChange={setTo}
          />
        </Field>
        <Field label={HISTORY_LABELS.reason} required>
          <TextArea value={note} onChange={(event) => setNote(event.target.value)} rows={3}  />
        </Field>
        <fieldset className="mc-fieldset">
          <legend>{P.returnedRequest}</legend>
          <Stack gap={4}>
            {FLAGGABLE.map((key) => (
              <CheckTile
                key={key}
                plain
                checked={flagged.includes(key)}
                onChange={(on) => setFlagged(on ? [...flagged, key] : flagged.filter((item) => item !== key))}
                hs={`req.flag.${key.replace(/_/g, "-")}`}
              >
                {FIELD_WORD[key] ?? key}
              </CheckTile>
            ))}
          </Stack>
        </fieldset>
      </Stack>
    </Dialog>
  );
}

